import './index.css'
import { MAIN, STEM } from '@disco/constants/Nesting'
import uniq from 'lodash/uniq'

import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import type { ItemWithUpdatedFields } from 'types/nesting'
import type { NestingLabel, Track, TrackId } from 'types/track'
import Mixpanel from '@disco/api/mixpanel'
import localProvider from '@disco/services/upload/providers/local'
import type { ManageNestingFormState } from '@disco/state-management/reducers/forms/manage-nesting'
import {
  getManageNestingForm,
  updateManageNestingForm,
} from '@disco/state-management/reducers/forms/manage-nesting'
import { getTracksWithUpdatedFields } from '@disco/state-management/selectors/nesting'
import { addNewTracksAndShiftPosition } from '@disco/utils/nesting'
import UploadOptions from 'components/common/upload/upload-options'
import DocumentHeadTags from 'components/document-head-tags'
import Heading from 'components/ui/heading'
import ModalWindow from 'components/ui/modal-window'
import Spinner from 'components/ui/spinner'
import Stack from 'components/ui/stack'
import ManageNestingFooterActions from './footer-actions'
import ManageNestingHeaderActions from './header-actions'
import ManageNestingTable from './table'
import type { NestingImportType } from './use-nesting-upload'
import { useNestingUpload } from './use-nesting-upload'

type Props = {
  mainTrack: Track
  nestedIds: TrackId[]
  tracksLoading: boolean
  error: Error | null | undefined
  onClose: (...args: Array<any>) => any
  defaultState?: Record<TrackId, ItemWithUpdatedFields>
}
type EventDetails = {
  type?:
    | 'ADD_NESTING_TRACK'
    | 'SAVE_NESTING_FORM'
    | 'REMOVE_TRACK'
    | 'ADD_LABEL'
    | 'CHANGE_LABEL'
  trackId?: number | null | undefined
  nestingLabel?: string
}

const ManageNesting = ({
  mainTrack,
  nestedIds,
  tracksLoading,
  error,
  onClose,
  defaultState = {},
}: Props): React.ReactNode => {
  const dispatchGlobal = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const formState: ManageNestingFormState = useSelector(getManageNestingForm)
  const [importType, setImportType] = useState<NestingImportType | null>(null)
  const [showImport, setShowImport] = useState<boolean>(false)
  useEffect(function initForm() {
    // Event #1 from https://disco-team.atlassian.net/browse/DISCO-4871
    Mixpanel.sendPureEvent('Open modal', {
      Modal: 'Manage nesting',
      Context: 'Nesting',
    })
    const initialState = {
      submitting: false,
      error: null,
      itemsWithUpdatedFields: {
        ...defaultState,
        [mainTrack.id]: {
          nesting_label: MAIN as NestingLabel,
          nesting_position: 0,
          nesting_parent_id: null,
        },
      },
      mainTrackId: mainTrack.id,
    }
    dispatchGlobal(updateManageNestingForm(initialState))
  }, [])
  const [trackIds, setTrackIds] = useState([])
  const nestedTracks = useSelector(state =>
    getTracksWithUpdatedFields(
      state,
      trackIds,
      formState.itemsWithUpdatedFields,
    ),
  )
  const updateForm = useCallback(
    (update: any, eventDetails?: EventDetails) => {
      dispatchGlobal(updateManageNestingForm(update))
      if (!eventDetails) return
      const { type, nestingLabel, trackId } = eventDetails

      switch (type) {
        case 'ADD_NESTING_TRACK':
          // Event #3 from https://disco-team.atlassian.net/browse/DISCO-4871
          Mixpanel.sendPureEvent('Add track', {
            Modal: 'Manage nesting',
            Context: 'Nesting',
          })
          break

        case 'SAVE_NESTING_FORM':
          // Event #2 from https://disco-team.atlassian.net/browse/DISCO-4871
          Mixpanel.sendPureEvent('Save modal', {
            Modal: 'Manage nesting',
            Context: 'Nesting',
          })
          break

        case 'REMOVE_TRACK':
          // Event #5 from https://disco-team.atlassian.net/browse/DISCO-4871
          Mixpanel.sendPureEvent('Remove track', {
            Modal: 'Manage nesting',
            Type: nestingLabel,
            ['Track ID']: trackId,
          })
          break

        case 'ADD_LABEL':
          // Event #4 from https://disco-team.atlassian.net/browse/DISCO-4871
          Mixpanel.sendPureEvent('Add label', {
            Modal: 'Manage nesting',
            Context: 'Nesting',
            Type: nestingLabel,
            ['Track ID']: trackId,
          })
          break

        case 'CHANGE_LABEL':
          // Event #4 from https://disco-team.atlassian.net/browse/DISCO-4871
          Mixpanel.sendPureEvent('Change label', {
            Modal: 'Manage nesting',
            Context: 'Nesting',
            Type: nestingLabel,
            ['Track ID']: trackId,
          })
          break

        default:
          break
      }
    },
    [dispatchGlobal],
  )
  const onFinishedUpload = useCallback(
    (tracks: Track[], uploadedImportType: NestingImportType) => {
      updateForm(
        addNewTracksAndShiftPosition({
          tracks: nestedTracks,
          newTracks: tracks.map(track => ({
            ...track,
            nesting_parent_id: formState.mainTrackId,
            nesting_label: uploadedImportType === STEM ? STEM : null,
          })),
        }),
      )
      setImportType(null)
      setShowImport(false)
    },
    [formState.mainTrackId, nestedTracks, updateForm],
  )
  const { upload, uploading } = useNestingUpload(onFinishedUpload)

  const onFilesDrop = async ({
    files,
    isStem,
  }: {
    files: File[]
    isStem?: boolean
  }) => {
    const droppedImportType = isStem ? STEM : MAIN
    upload(files, localProvider, droppedImportType, {
      temporary: true,
      title: 'Nesting Upload',
    })
    setImportType(droppedImportType)
  }

  useEffect(() => {
    if (!tracksLoading) {
      setTrackIds(
        uniq([
          ...(nestedIds || []),
          ...Object.keys(formState.itemsWithUpdatedFields).map(Number),
        ]),
      )
    }
  }, [tracksLoading, nestedIds, formState])
  useEffect(
    function updateLocationString() {
      if (!formState?.mainTrackId) return
      const targetPath = `/explorer/tracks/${formState.mainTrackId}/manage-nesting`
      if (location.pathname === targetPath) return
      navigate(targetPath, { replace: true })
    },
    [dispatchGlobal, formState?.mainTrackId, location.pathname, navigate],
  )
  const handleClose = useCallback(() => {
    if (importType) {
      setImportType(null)
      setShowImport(false)
    } else {
      onClose()
    }
  }, [importType, onClose])
  return (
    <>
      <DocumentHeadTags
        title={`Manage nesting for ${mainTrack?.name} | DISCO`}
      />
      <ModalWindow
        header={
          <Heading>
            {!showImport
              ? 'Manage nesting'
              : `Import nested ${importType === STEM ? 'stems' : 'versions'}`}
          </Heading>
        }
        contextualHeader={
          <ManageNestingHeaderActions
            formState={formState}
            nestedTracks={nestedTracks}
            updateForm={updateForm}
            tracksLoading={tracksLoading}
            error={error}
            hideButtons={showImport}
          />
        }
        actions={
          !importType ? (
            <ManageNestingFooterActions
              formState={formState}
              updateForm={updateForm}
              onClose={handleClose}
            />
          ) : null
        }
        isOpen
        onClose={handleClose}
        width={showImport ? 'medium' : 'large'}
      >
        {!importType || uploading ? (
          <Stack space="xsmall">
            <div className="ManageNesting">
              <ManageNestingTable
                tracksLoading={tracksLoading}
                formState={formState}
                nestedTracks={nestedTracks}
                updateForm={updateForm}
                onFilesDrop={onFilesDrop}
                onClickImport={isStem => {
                  setImportType(isStem ? STEM : MAIN)
                  setShowImport(true)
                }}
              />
              {uploading && ( // TODO: update "uploading state design" if needed
                <div className="ManageNesting__Overlay">
                  <Spinner />
                </div>
              )}
            </div>
          </Stack>
        ) : (
          <div className="ManageNesting__Import">
            <UploadOptions
              onPickedFiles={(files, provider) => {
                upload(files, provider, importType, {
                  temporary: true,
                  title: 'Nesting Upload',
                  context: 'nesting',
                })
                setShowImport(false)
              }}
            />
          </div>
        )}
      </ModalWindow>
    </>
  )
}

export default ManageNesting
